import React, { forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { saveEmailFailure, saveEmailSuccess, isEmailEdited as setEmailEdited, isSubjectEdited as setSubjectEdited, isPresave, autoThreadingToggled as setAutoThreadingToggled, savingToTimelineToggled as setSavingToTimelineToggled, setEmailSalesforceIds, selectRecords, updateRecords, activityTimelineSaved } from '../../actions/mail';
import { setFilesSaveJobs, setDocrioFiles, setSavedCount, setSaveLocationAdded } from '../../actions/files';
import * as RootActions from '../../actions/index';
import { changeAlertText } from '../../actions/index';
import { useDispatch, useSelector, shallowEqual, batch } from 'react-redux';
import { saveEmail, getEmailDetails, getEmail } from '../../../api/emailsApi/emails';
import { postFilesComplete } from '../../../api/emailsApi/files';
import { logError } from '../../utils/request';
import { useAsyncInterval } from '../../utils/hooks';
import { runFileValidation } from '../../utils/fileValidation';
import officeApi from '../../../api/officeApi';
import { isDesktop } from '../../utils/environment';
import { delay } from '../../utils/delay';
import { handleAddTime, getProcessingTimeRecords } from '../../utils/time';
import { setTimeRecords, showTimeReminder, addingTime, setTimeProcessing } from '../../actions/time';
import { addTime, checkBillingRules } from '../../../api/emailsApi/time';
import { Tooltip } from '../shared/Tooltip';
import { getToastNotification } from './SuccessMessages';

import './Save.scss';

const MAX_RETRIES = 15;

const Save = forwardRef(({ timeRecordObject, saveAll, handleClick }, ref) => {
  const dispatch = useDispatch();
  const mail = useSelector((state) => state.mail, shallowEqual);
  const { isComposingEmail, settings, isSavingToTimeline, selectedRecords, timelineSubject, activeEmail, draftId, isEmailSaved, presave, isEmailEdited, isSubjectEdited, emails, autoThreadingToggled, savingToTimelineToggled } = mail;
  const { isLoading } = useSelector((state) => state.app);
  const isSyncingAttachments = useSelector((state) => state.app.isSyncingAttachments);
  // mail state
  const emailSalesforceIds = useSelector((state) => state.mail.emailSalesforceIds) || [];
  const removalJobId = useSelector((state) => state.mail.removalJobId);
  const ownerEmail = useSelector((state) => state.mail.ownerEmail);
  // files state
  const docrioFiles = useSelector((state) => state.files.docrioFiles);
  const savedFileCount = useSelector((state) => state.files.savedFileCount);
  const filesSaveJobIds = useSelector((state) => state.files.filesSaveJobIds);
  const saveLocationAdded = useSelector((state) => state.files.saveLocationAdded);
  const fileValidationErrors = useSelector((state) => state.files.fileValidationErrors);
  const fileValidationPending = useSelector((state) => state.files.fileValidationPending);
  // Only block the Save button while a validation is in flight (so we know which files to exclude).
  // Rule-violating files are dropped from the upload rather than blocking the whole email save.
  const isSaveBlockedByValidation = !!fileValidationPending;
  // folders state
  const folderMaps = useSelector((state) => state.folders.folderMaps);
  // time state
  const isErrorLoadingTimeConfig = useSelector((state) => state.time.isErrorLoadingTimeConfig);
  const timeRecords = useSelector((state) => state.time.timeRecords);
  const isAddingTime = useSelector((state) => state.time.isAddingTime);
  const isProcessingTime = useSelector((state) => state.time.timeProcessing);
  const sendTimeOnSave = useSelector((state) => state.time.sendOnSave);
  const objectsConfiguredForTime = useSelector((state) => state.time?.config?.objectsConfiguredForTime) || [];
  const timeObjectConfigured = useSelector((state) => state.time?.config?.objectsConfiguredForTime)?.find((obj) => Object.values(selectedRecords)?.map((record) => record?.type)?.includes(obj));
  const timeConfigFields = useSelector((state) => state.time.config?.fields);
  // settings state
  const isSavingAllAttachments = settings?.docrioSettings?.attachments?.saveAllAttachments;
  const saveAllEmailSettings = settings?.docrioSettings?.emailFiles?.saveAllEmails;
  const defaultFolderSettings = settings?.docrioSettings?.defaultFolderSettings;
  const timeEnabled = settings?.timeSettings?.timeEnabled;
  const timeMode = settings?.timeSettings?.timeTrackingMode;
  const timeNamespace = settings?.override?.timeNamespace || 'litify_time';
  const docrioNamespace = settings?.override?.docrioNamespace || 'litify_docs';

  // time variables
  const timeToSave = timeRecordObject ? [...timeRecords.filter((r) => !r.saved), timeRecordObject] : timeRecords.filter((r) => !r.saved);
  const timeRecordsToBeSaved = timeToSave.length > 0;
  const timeSaved = timeRecords.filter((r) => r.saved && !r.errorMessage);
  const selectedRecordsForTime = Object.fromEntries(Object.entries(selectedRecords).filter(([key, obj]) => objectsConfiguredForTime.includes(obj.type))) || {};

  // files variables
  const filesToSave = isComposingEmail ?
    docrioFiles.filter((file) => file.saved || file.presaved || file.toSave) :
    docrioFiles.filter((file) => file.toSave && !file.saved && !file.presaved && Object.keys(file.folderPaths || {}).length);
  const newFilesToSave = isComposingEmail ? !!(filesToSave.filter((file) => file.toSave).length) : !!filesToSave.length;

  // In compose mode, we need to track if saved files have been added or removed.
  const haveFilesBeenRemoved = isComposingEmail && savedFileCount > filesToSave.length;
  const isSaveEnabled = savingToTimelineToggled || newFilesToSave || haveFilesBeenRemoved || isSubjectEdited || timeRecordsToBeSaved || autoThreadingToggled;
  const filesToSaveInComposeMode = docrioFiles.filter((file) => file.toSave && !file.saved && !file.presaved && Object.keys(file.folderPaths || {}).length);

  // email/Activity Timeline variables
  const allEmailsSaved = !Object.values(isEmailSaved).includes(false);

  // object containing the current save state
  const currentSaveState = {
    isComposingEmail,
    allEmailsSaved,
    isSavingToTimeline,
    timeSaved,
    timeToSave,
    filesToSave,
  };

  // useStates
  const [disabled, setDisabled] = useState(!isSaveEnabled);
  const [saving, setSaving] = useState(!!filesSaveJobIds.length);
  const [checkingSaveStatus, setCheckingSaveStatus] = useState(false);
  const [processingTimeLocal, setProcessingTimeLocal] = useState(false);

  // string constants
  const numFiles = filesToSave.length > 1 ? 'Files' : 'File';
  const tooltipTextContent = `Saves Email${filesToSave.length ? `, Time & ${numFiles}` : ' & Time'}`;
  const getEmlName = (name) => (name?.split('.').pop() !== 'eml' ? `${name || '(No Subject)'}.eml` : name);

  // Poll for save status
  useAsyncInterval(async () => {
    if (!checkingSaveStatus) {
      await checkSavingStatus();
    }
    if (!processingTimeLocal) {
      await checkProcessingTime();
    }
  }, saving ? 500 : null);

  // Poll for time processing status
  const checkProcessingTime = async () => {
    if (isProcessingTime) {
      setProcessingTimeLocal(true);
      try {
        const records = await getProcessingTimeRecords(emailSalesforceIds, activeEmail || draftId, timeNamespace, dispatch, ownerEmail);
        if (records.every((record) => record.saved || record.errorMessage)) {
          if (!docrioFiles.find((file) => file.isSaving)) {
            setSaving(false);
          }
          dispatch(setTimeRecords(records));
          dispatch(setTimeProcessing(false));
        }
      } catch (e) {
        console.error('Error checking time processing:', e);
        dispatch(setTimeProcessing(false));
        dispatch(changeAlertText('Error checking for time save status.', 'error'));
      }
      setProcessingTimeLocal(false);
    }
  };

  // Add WarningIds / ErrorIds if save was triggered in TimeFormContainer
  const patchTimeRecordObject = async () => {
    const response = (await checkBillingRules(timeRecordObject))?.data;
    const responseWarningIds = response?.warningIdsEncoded;
    const responseErrorIds = response?.errorIdsEncoded;
    if (responseWarningIds) {
      timeRecordObject[`${timeNamespace}__lit_Warning_Rules__c`] = responseWarningIds;
    }
    if (responseErrorIds) {
      timeRecordObject[`${timeNamespace}__lit_Error_Rules__c`] = responseErrorIds;
    }
    return [...timeRecords.filter((r) => !r.saved), timeRecordObject];
  };

  // Get the saved email data after saving
  const getSavedEmailData = async () => {
    const emailResponse = await getEmail(activeEmail || draftId, '', true, ownerEmail);
    const emailAndDisplayRecords = emailResponse.data;
    let responseRecordsFromSF = [];

    if (emailAndDisplayRecords.EmailMessage) { // Pre emails v5 SF response
      responseRecordsFromSF = [emailAndDisplayRecords.EmailMessage];
    } else if (emailAndDisplayRecords.Results) { // emails v5 SF response
      responseRecordsFromSF = Object.values(emailAndDisplayRecords.Results);
    }
    const savedStatusObject = {};
    const selectedRecords = [];
    if (responseRecordsFromSF.length) {
      responseRecordsFromSF.forEach((relatedTo) => {
        savedStatusObject[relatedTo.id] = relatedTo.emailMessageRecordSaved;
        selectedRecords.push({ ...relatedTo, Id: relatedTo.id, type: relatedTo.relatedToApiName, autothreading: emailAndDisplayRecords.threadSave?.includes(relatedTo.id), ...relatedTo.displayRecords[0] }); // there is only display record here i think
      });
      dispatch(updateRecords(selectedRecords));
      dispatch(activityTimelineSaved(savedStatusObject));
    }
  };

  const checkSavingStatus = async () => {
    if (filesSaveJobIds.length) {
      setCheckingSaveStatus(true);
      const fileStatuses = await postFilesComplete(activeEmail, JSON.stringify({ ids: filesSaveJobIds }), ownerEmail);
      const jobs = fileStatuses.data.jobs;
      if (!jobs.find((status) => status.jobStatus === 'PENDING') && !isProcessingTime) {
        setSaving(false);
      }
      const completedJobs = jobs.filter((statuses) => statuses.jobStatus === 'COMPLETED').map((job) => Object.values(job.report)).flat(Infinity);
      const filesToUpdate = [];
      docrioFiles.forEach((attachment) => {
        if (!completedJobs.length) return;
        const file = completedJobs.find((job) => job.hash === attachment.hash);
        const updatedFile = file?.status === 'SUCCESS' ?
        {
          ...attachment,
          saved: true,
          toSave: false,
          isSaving: false,
          docrioId: file.docrioId,
        } : file?.status === 'FAILED' ?
        {
          ...attachment,
          saveFailed: true,
          saved: false,
          isSaving: false,
        } : { ...attachment, isSaving: false };
        filesToUpdate.push(updatedFile);
      });
      if (filesToUpdate.length) {
        dispatch(setDocrioFiles(filesToUpdate));
        await getSavedEmailData();
      }
      setCheckingSaveStatus(false);
    }
  };

  useEffect(() => {
    // all saved implies all attachments are either saved, presaved, or have a docrioId.
    const allSaved = docrioFiles.every((file) => file.saved || file.presaved) && allEmailsSaved && !timeRecordsToBeSaved;

    if (isLoading) {
      setDisabled(true);
    } else if (isComposingEmail) {
      if (saveLocationAdded) {
        setDisabled(false);
      } else if ((!isEmailEdited || (isEmailEdited && presave && !isSubjectEdited)) &&
        (!newFilesToSave || allSaved) &&
        !savingToTimelineToggled &&
        !haveFilesBeenRemoved &&
        !timeRecordsToBeSaved &&
        !autoThreadingToggled) {
        setDisabled(true);
      } else if (!presave && filesToSaveInComposeMode?.length === 0 && !isSavingToTimeline && !timeRecordsToBeSaved) {
        setDisabled(true);
      } else if (presave && (savingToTimelineToggled || autoThreadingToggled)) {
        setDisabled(false);
      } else {
        setDisabled(!isSaveEnabled);
      }
    } else if (allSaved && !autoThreadingToggled) {
      setDisabled(true);
    } else if (removalJobId) {
      setDisabled(true);
    } else if (isProcessingTime) {
      setDisabled(true);
    } else if (allSaved && (autoThreadingToggled || savingToTimelineToggled)) {
      setDisabled(false);
    } else {
      setDisabled(!(isSaveEnabled || isSavingToTimeline));
    }
  }, [filesToSaveInComposeMode, isSavingToTimeline, filesToSave, docrioFiles, isEmailSaved, isEmailEdited, isSubjectEdited, presave, removalJobId, isProcessingTime, isLoading, saveLocationAdded, autoThreadingToggled]);

  useEffect(() => {
    if (docrioFiles.find((file) => file.isSaving) || isProcessingTime) {
      setSaving(true);
    } else {
      setSaving(false);
    }
  }, [isProcessingTime, emailSalesforceIds]);


  const isEml = (id) => {
    return [activeEmail, draftId].includes(id);
  };


  // reformat the folder path to be saved to salesforce
  const formatFolderPath = (recordId, path, name) => {
    /*
      If the route field is found in the folderMaps, return route
      If no path, return default []
      If path is parsable, it can be returned under the assumption it is a valid path.
      If path is not parsable, split then stringify.
    */
    try {
      if (folderMaps?.[recordId]?.[path]?.route) {
        return JSON.stringify(folderMaps?.[recordId]?.route);
      }
      return path ? JSON.parse(path) && path : '[]';
    } catch {
      if (path === name) {
        return JSON.stringify([path]);
      } else if (name.indexOf('/') > -1) {
        const [parentPath] = path.split(`/${name}`);
        return JSON.stringify(parentPath.split('/').concat(name));
      } else {
        return JSON.stringify(path.split('/'));
      }
    }
  };

  // Determine the category of the file using the file's id and docrioId
  const getFileCategory = (file) => {
    if (isEml(file.id)) {
      return 'eml';
    } else if (file.docrioId) {
      // if the file already exists as an attachment, return the fileAttachment category
      const fileHasBeenSaved = !!Object.values(selectedRecords)?.find((record) => !!record?.attachments?.find((attachment) => attachment[`${docrioNamespace}__File_Info__c`] === file.docrioId));
      return fileHasBeenSaved ? 'fileAttachments' : 'docrioAttachments';
    } else {
      return 'fileAttachments';
    }
  };

  // create the fileRecords object to be sent to the PUT endpoint
  const handlePreSaveFiles = async (validationErrors = {}) => {
    if (!filesToSave.length) {
      return [];
    }
    // A file that violates a save-time rule is excluded from the upload; the email and its valid
    // attachments still process normally.
    const isViolating = (fileId) => !!validationErrors?.[fileId]?.length;

    const fileRecords = {};
    try {
      filesToSave.forEach((file) => {
        // Skip files that violate a save-time rule - they are not created in Salesforce, while the
        // email and its valid attachments still process normally.
        if (isViolating(file.id)) {
          return;
        }
        Object.entries(file.folderPaths).forEach(([recordId, folderPathObject]) => {
          // initialize the record object if it doesn't exist
          if (!fileRecords[recordId]) {
            fileRecords[recordId] = {
              eml: {},
              fileAttachments: [],
              docrioAttachments: [],
            };
          }
          // setup variables for the file
          const fileCategory = getFileCategory(file);
          const recordType = selectedRecords[recordId]?.type || '';
          const emlPath = (folderPathObject?.path || folderPathObject?.path === '' ? folderPathObject?.path : defaultFolderSettings[recordType]?.saveEmailFolderPath) || '';
          const attachmentPath = (folderPathObject?.path || folderPathObject?.path === '' ? folderPathObject?.path : defaultFolderSettings[recordType]?.saveAttachmentsFolderPath) || '';
          const attachmentPathName = (folderPathObject?.name ? folderPathObject?.name : defaultFolderSettings[recordType]?.objectLabel + ' Home') || '';

          if (fileCategory === 'eml') {
            fileRecords[recordId][fileCategory] = {
              Name: getEmlName(file.name),
              Location: formatFolderPath(recordId, emlPath, attachmentPathName),
            };
          } else if (file.docrioId) { // if the file has a docrioId, it is a docrio attachment
            fileRecords[recordId].docrioAttachments.push({
              Name: file.name,
              Location: formatFolderPath(recordId, attachmentPath, attachmentPathName),
              Hash: file.hash,
              AlternativeHash: file.alternativeHash,
              Id: file.docrioId,
            });
          } else { // if the file is not an eml or docrio attachment, it is a file attachment
            fileRecords[recordId].fileAttachments.push({
              Name: file.name,
              Location: formatFolderPath(recordId, attachmentPath, attachmentPathName),
              Hash: file.hash,
              AlternativeHash: file.alternativeHash,
            });
          }
        });
      });
    } catch (error) {
      console.error('Errors preparing attachments for saving:', error);
      dispatch(changeAlertText('Failed to pre-save files', 'error'));
      return [];
    }

    let savedFileCount = 0;
    const newDocrioFiles = docrioFiles.map((attachment) => {
      // Leave violating files unsaved so the violation persists and they aren't marked as saved.
      if (isViolating(attachment.id)) {
        return { ...attachment, toSave: false, saved: false };
      }
      savedFileCount++;
      const updatedAttachment = {
        ...attachment,
        toSave: false,
        saved: true,
      };
      return updatedAttachment;
    });
    batch(() => {
      dispatch(setDocrioFiles(newDocrioFiles));
      dispatch(setSavedCount(savedFileCount));
    });
    return fileRecords;
  };

  const handlePreSaveTime = async () => {
    if (!timeRecordsToBeSaved) {
      return;
    }

    const newlySaved = [];
    for (let index = 0; index < timeToSave.length; index++) {
      let timeRecord = timeToSave[index];
      let response = {};
      if (saveAll) {
        response = (await checkBillingRules(timeRecord))?.data;
        response['fixLater'] = true;
      }
      timeRecord = { ...timeRecord, ...response, saved: true };
      newlySaved.push(timeRecord);
    }

    batch(() => {
      if (addingTime) {
        dispatch(addingTime(false));
        dispatch(RootActions.clearAlertText());
      }
      dispatch(setTimeRecords([...timeRecords.filter((r) => r.saved), ...newlySaved]));
    });
    return saveAll ? newlySaved : [];
  };

  /**
   * updates and reformats the time records to be saved to docrio
   * @return {Promise<object>} - the file records to be save to docrio
   */
  const handleDocrioSave = async (validationErrors = {}) => {
    if (!filesToSave.length) {
      return {};
    }
    // A file that violates a save-time rule is excluded from the upload; the email and its valid
    // attachments still process normally.
    const isViolating = (fileId) => !!validationErrors?.[fileId]?.length;

    const fileRecords = {};

    try {
      filesToSave.forEach((file) => {
        // Skip files that violate a save-time rule - they are not created in Salesforce, while the
        // email and its valid attachments still process normally.
        if (isViolating(file.id)) {
          return;
        }
        Object.entries(file.folderPaths).forEach(([recordId, folderPathObject]) => {
          // initialize the record object if it doesn't exist
          if (!fileRecords[recordId]) {
            fileRecords[recordId] = {
              eml: {},
              fileAttachments: [],
              docrioAttachments: [],
            };
          }
          const type = selectedRecords[recordId]?.type || '';

          // if the record has this file already saved then skip it
          if (selectedRecords[recordId]?.attachments?.find((attachment) => attachment?.[`${docrioNamespace}__File_Data_Hash__c`] === file.hash)) {
            return;
          } else {
            const fileCategory = getFileCategory(file);
            if (fileCategory === 'eml') {
              const path = (folderPathObject?.path || folderPathObject?.path === '' ? folderPathObject?.path : defaultFolderSettings[type]?.saveAttachmentsFolderPath) || '';
              const pathName = (folderPathObject?.name ? folderPathObject?.name : defaultFolderSettings[type]?.objectLabel + ' Home') || '';
              fileRecords[recordId][fileCategory] = {
                Name: getEmlName(file.name),
                Id: file.id,
                Location: formatFolderPath(recordId, path, pathName),
                Hash: file.hash,
                AlternativeHash: file.alternativeHash,
                contentType: file.contentType || undefined,
                '@odata.type': file['@odata.type'],
                exchangeInfo: { fromDisplayName: emails.from.displayName, emailAddress: emails.emailAddress },
              };
            } else {
              const path = (folderPathObject?.path || folderPathObject?.path === '' ? folderPathObject?.path : defaultFolderSettings[type]?.saveAttachmentsFolderPath) || '';
              const pathName = (folderPathObject?.name ? folderPathObject?.name : defaultFolderSettings[type]?.objectLabel + ' Home') || '';
              fileRecords[recordId][getFileCategory(file)].push({
                Name: file.name,
                Location: formatFolderPath(recordId, path, pathName),
                Hash: file.hash,
                AlternativeHash: file.alternativeHash,
                Id: file.id || null,
                contentType: file.contentType || undefined,
                '@odata.type': file['@odata.type'], // Needed to determine the correct query for content in processPostSaveFiles
                exchangeInfo: { fromDisplayName: emails.from.displayName, emailAddress: emails.emailAddress },
              });
            }
          }
        });
      });
    } catch (error) {
      console.error('Errors preparing attachments for saving:', error);
      dispatch(changeAlertText('Failed to pre-save files', 'error'));
      return {};
    }

    let savedFileCount = 0;
    const newDocrioFiles = docrioFiles.map((attachment) => {
      // Violating files are excluded from the upload, so don't flag them as saving.
      if (isViolating(attachment.id)) {
        return { ...attachment, isSaving: false, saved: false };
      }
      savedFileCount++;
      const updatedAttachment = {
        ...attachment,
        isSaving: Object.keys(attachment.folderPaths).length > 0,
        saved: false,
      };
      return updatedAttachment;
    });

    batch(() => {
      dispatch(setDocrioFiles(newDocrioFiles));
      dispatch(setSavedCount(savedFileCount));
    });
    return fileRecords;
  };

  /**
   * updates and reformats the time records to be saved to docrio
   * has no return value because it is updating the variable passed in as a parameter
   * @param {array} timeRecordArr - the time records to be saved to docrio
   * @return {void}
   */
  const formatTimeRecords = (timeRecordArr) => {
    for (const timeRecord of timeRecordArr) {
    // format time records to be saved with the errorIdsEncoded and warningIdsEncoded fields if they exist
      const updatedTimeRecord = {
        ...timeRecord,
        saved: true,
      };

      // Clean up the properties you don't want to keep
      delete updatedTimeRecord.errorIdsEncoded;
      delete updatedTimeRecord.warningIdsEncoded;
      delete updatedTimeRecord.errorRules;
      delete updatedTimeRecord.warningRules;
      delete updatedTimeRecord.fixLater;

      // Rename properties if needed
      if (timeRecord.errorIdsEncoded) {
        updatedTimeRecord[`${timeNamespace}__lit_Error_Rules__c`] = timeRecord.errorIdsEncoded;
      }
      if (timeRecord.warningIdsEncoded) {
        updatedTimeRecord[`${timeNamespace}__lit_Warning_Rules__c`] = timeRecord.warningIdsEncoded;
      }
      const relatedRecord = selectedRecords[updatedTimeRecord[`${timeNamespace}__lit_Related_Record_ID__c`]];
      if (relatedRecord.timeRecords) {
        relatedRecord.timeRecords.push(updatedTimeRecord);
      } else {
        relatedRecord.timeRecords = [updatedTimeRecord];
      }
    }
  };

  /**
   * updates and reformats the time records to be saved to docrio
   * @param {object} fileRecords - the file records to be saved to docrio
   * @param {array} timeRecords - the time records to be saved to docrio
   * @return {Promise<void>}
   */
  const handleEmailSave = async (fileRecords, timeRecords = []) => {
    const alertText = getToastNotification(currentSaveState, true);
    dispatch(changeAlertText(alertText));
    const newTimeToSave = timeRecordObject ? await patchTimeRecordObject() : timeToSave;
    timeRecords = timeRecords.length ? timeRecords : newTimeToSave;
    if (timeEnabled) {
      formatTimeRecords(timeRecords);
    }
    const body = {
      preSave: isComposingEmail,
      emailSubject: timelineSubject || '',
      relatedTos: Object.values(selectedRecords).map((record) => {
        const docrioSave = fileRecords[record.Id] && (fileRecords[record.Id].docrioAttachments.length > 0 || fileRecords[record.Id].fileAttachments.length > 0);
        const isAutoThreadingToggle = !isComposingEmail && autoThreadingToggled && !docrioSave && !fileRecords[record.Id]?.eml?.Name && !record.timeRecords?.length && isEmailSaved[record.Id];

        // Don't save this to dynamo so files/folders load if presave is loaded again
        delete record.fetchedFolders;
        delete record.fetchingFiles;
        delete record.fetchedFiles;

        const { autothreading, timeRecords, ...displayFields } = record;

        return {
          id: record.Id,
          type: record.type,
          displayFields: record?.displayFields || displayFields,
          // if this call is for toggled autothread, then we check for attachments to see if we should save emails and files to docrio
          docrioSave: isAutoThreadingToggle ? !!record.attachments : !!(isSavingAllAttachments || docrioSave),
          emailSave: isAutoThreadingToggle ? !!record.attachments : (saveAllEmailSettings || !!fileRecords[record.Id]?.eml?.Name),
          threadSave: !isAutoThreadingToggle ? !!autothreading : undefined,
          autoThreadingToggledValue: isAutoThreadingToggle ? !!autothreading : undefined,
          isSavingToTimeline: isSavingToTimeline || (allEmailsSaved && !isComposingEmail),
          attachments: fileRecords[record.Id] || {
            eml: {},
            fileAttachments: [],
            docrioAttachments: [],
          },
          timeRecords: timeRecords || [],
        };
      }),
    };

    const exchangeEmailId = activeEmail || draftId;
    let saveResponse;
    try {
      saveResponse = await saveEmail(exchangeEmailId, body, ownerEmail);
    } catch (error) {
      logError(error);
      const attachmentsDup = docrioFiles.map((attachment) => ({ ...attachment, isSaving: false }));
      batch(() => {
        dispatch(setDocrioFiles(attachmentsDup));
        dispatch(saveEmailFailure(error));
        dispatch(changeAlertText('Failed to save email', 'error'));
      });
      return;
    }
    // Handle v5 response errors
    const saveResponseData = JSON.parse(saveResponse.data.response || '{}');

    // handle v5 errors
    let areEmailMessageErrors = false;
    if (saveResponseData?.results) {
      for (const [key, value] of Object.entries(saveResponseData.results)) {
        if (value.EmailMessageError) {
          areEmailMessageErrors = true;
          const selectedRecord = selectedRecords[key];
          selectedRecord.emailMessageError = value.EmailMessageError;
        }
      }
      if (areEmailMessageErrors) {
        dispatch(selectRecords(Object.values(selectedRecords)));
      }
    }

    let timeSaveErrors = false;
    let timeSaveRecords;
    // create emailMEssageToRelatedTO object to save time records if we are not composing and have time records to save
    if (!isComposingEmail && timeRecordsToBeSaved) {
      let emailMessageToRelatedTo = {};
      // handle the v4 response
      if (saveResponseData?.EmailMessage?.id) {
        emailMessageToRelatedTo = {
          [saveResponseData?.EmailMessage?.id]: saveResponseData?.EmailMessage?.relatedToId,
        };
      } else {
        // handle v5 response
        emailMessageToRelatedTo = Object.values(saveResponseData.results)?.reduce((acc, cur) => {
          acc[cur.EmailMessage?.id] = cur.EmailMessage?.relatedToId;
          return acc;
        }, {});
      }
      // save the emailMessageToRelatedTo object to the store
      dispatch(setEmailSalesforceIds(Object.keys(emailMessageToRelatedTo)));

      const timeRecordsObject = {};
      // reformat the time records to be saved to salesforce
      for (const emailMessageSalesforceId of Object.keys(emailMessageToRelatedTo)) {
        const requestTimeRecords = [];
        const timeRecordsForEmail = timeToSave.filter((record) => record[`${timeNamespace}__lit_Related_Record_ID__c`] === emailMessageToRelatedTo[emailMessageSalesforceId]);
        for (const record of timeRecordsForEmail) {
          // reformat the errorIdsEncoded and warningIdsEncoded fields to be saved to salesforce
          if (record.errorIdsEncoded) {
            record[`${timeNamespace}__lit_Error_Rules__c`] = record.errorIdsEncoded;
          }
          if (record.warningIdsEncoded) {
            record[`${timeNamespace}__lit_Warning_Rules__c`] = record.warningIdsEncoded;
          }
          // delete the fields that are not needed to be saved to salesforce
          delete record.errorIdsEncoded;
          delete record.warningIdsEncoded;
          delete record.errorRules;
          delete record.warningRules;
          delete record.fixLater;
          requestTimeRecords.push(record);
        }
        timeRecordsObject[emailMessageSalesforceId] = requestTimeRecords;
      }
      const addTimeResponse = await addTime(timeRecordsObject, exchangeEmailId, ownerEmail);

      // collect all the saved time records
      timeSaveRecords = Object.values(addTimeResponse.data.timeRecords).flat();
      if (addTimeResponse.status !== 200) {
        dispatch(setTimeProcessing(false));
        timeSaveErrors = true;
      }
      // collect all the time records that failed and update the store
      const failedSaveTimeRecords = timeSaveRecords.filter((t) => t.errorMessage);
      const successfulTimeRecords = timeSaveRecords.filter((t) => !t.errorMessage);
      dispatch(setTimeRecords([...failedSaveTimeRecords, ...successfulTimeRecords, ...timeSaved]));
      setSaving(true);
      dispatch(setTimeProcessing(true));
      dispatch(RootActions.isLoading(false));
    }

    batch(() => {
      if (areEmailMessageErrors) {
        dispatch(changeAlertText('Some records failed to save, view errors below.', 'error'));
      }
      if (isSavingToTimeline) {
        dispatch(saveEmailSuccess());
      } else {
        dispatch(setEmailEdited(false));
        dispatch(setSubjectEdited(false));
      }
      if (isComposingEmail) {
        dispatch(isPresave(true)); // toggle presave flag when files have been presaved
      }
      if (saveResponse.data.jobId) {
        !isComposingEmail && setSaving(true);
        dispatch(setFilesSaveJobs([saveResponse.data.jobId]));
      }
      if (!timeSaveErrors && !areEmailMessageErrors) {
        if (isAddingTime) {
          dispatch(addingTime(false));
          dispatch(RootActions.clearAlertText());
        }
        const successMessage = getToastNotification({
          ...currentSaveState,
          isEmailSaved: isSavingToTimeline,
          timeSaved: (timeRecords || []).filter((t) => t.saved),
          timeToSave: (timeRecords || []).filter((t) => !t.saved),
        });
        dispatch(changeAlertText(successMessage));
      }
      dispatch(setSavingToTimelineToggled(false));
      dispatch(setAutoThreadingToggled(false));
      dispatch(setSubjectEdited(false));
    });
    // if only AT is saved, then get the email now so app rerenders and popout links works
    if (!timeSaveErrors && !areEmailMessageErrors && isSavingToTimeline && !saveResponse.data.jobId) {
      await getSavedEmailData();
    }
  };

  /**
   * Polls graph 10 times over a 10 second window to verify that graph is in sync
   * with the front end.
   * @param {number} loop Number of loop iterations
   * @return {undefined}
   * @throws Error if unable to find email, or email does not match
   */
  const waitForGraphUpdate = async (loop = 0) => {
    let isMatch;
    try {
      isMatch = await checkGraphForEmail();
    } catch (e) {
      console.error(e);
      throw new Error('Error connecting to Exchange server.');
    }
    if (isMatch) {
      return;
    } else if (loop < MAX_RETRIES) {
      console.log(`Email not found. Searching again...`);
      await delay(1);
      return await waitForGraphUpdate(loop + 1);
    } else {
      throw new Error('Email is not synced with Exchange server.');
    }
  };

  /**
   * Checks to see if the email is in graph, and contains the expected information
   * @return {bool} whether or not the email is updated in graph
   * @throws Error if there's a problem connecting to graph
   */
  const checkGraphForEmail = async () => {
    const response = await getEmailDetails(draftId, ownerEmail);
    const subject = await officeApi.getSubject();
    return response.subject === subject;
  };

  const handleSave = async (fromFixLater) => {
    if (handleClick) {
      try {
        // must check for equals don't remove === true
        await handleClick(fromFixLater === true);
      } catch (e) {
        console.error(e);
        return;
      }
    }

    if (timeMode === 'timeRequired' && timeObjectConfigured && !allEmailsSaved && !timeRecordsToBeSaved) {
      handleAddTime(isErrorLoadingTimeConfig, dispatch, true, false, selectedRecordsForTime, timeConfigFields, settings?.override?.pmNamespace || 'litify_pm');
      return;
    }

    try {
      dispatch(RootActions.isLoading(true));
      setDisabled(true);
      let fileRecords;
      let timeRecords = [];
      // If a validation is still in flight (or was just triggered), run it now and use the fresh
      // result so rule-violating files are reliably excluded - even when Save is invoked
      // programmatically (e.g. the time flow) and bypasses the button's disabled state.
      const saveValidationErrors = fileValidationPending
        ? await runFileValidation(docrioFiles, selectedRecords, ownerEmail, dispatch, undefined, {
          saveAllAttachments: isSavingAllAttachments,
          saveAllEmails: saveAllEmailSettings,
          defaultFolderSettings,
          emailFileIds: new Set([activeEmail, draftId].filter(Boolean)),
        })
        : (fileValidationErrors || {});
      if (isComposingEmail) {
        await officeApi.saveEmail();
        isDesktop() && (await waitForGraphUpdate());
        fileRecords = await handlePreSaveFiles(saveValidationErrors);
        timeRecords = timeEnabled ? await handlePreSaveTime() : [];
      } else {
        fileRecords = await handleDocrioSave(saveValidationErrors);
      }
      await handleEmailSave(fileRecords, timeRecords);
      dispatch(setSaveLocationAdded(false));
      if (timeMode === 'timeReminder' && timeObjectConfigured && !timeRecordsToBeSaved) {
        dispatch(showTimeReminder(true));
      }

      dispatch(RootActions.isLoading(false));
    } catch (e) {
      console.error(e);
      dispatch(RootActions.isLoading(false));
      dispatch(changeAlertText('Error Saving, Please try again.', 'error'));
      setDisabled(false);
    }
  };

  useImperativeHandle(ref, () => ({
    handleSave, // Expose the handleSave function
  }));

  const buttonText = saveAll ? 'Save All' : (isAddingTime && sendTimeOnSave && allEmailsSaved) ? 'Save Time' : 'Save';
  return (
    <div className={ isAddingTime ? 'time-save-component' : 'save-component' }>
      {isSyncingAttachments && <div className={'save-alertText'}>{'Cannot save while attachments are loading to Docrio Email.'}</div>}
      { saveAll ?
        <Tooltip textContent={tooltipTextContent}>
          <button
            className='save-button'
            data-testid='save-button-all'
            onClick={handleSave}
            disabled={disabled || saving || isSyncingAttachments || isSaveBlockedByValidation}
          >
            { buttonText }
          </button>
        </Tooltip> :
      <button
        className='save-button'
        data-testid='save-button'
        onClick={handleSave}
        disabled={disabled || saving || isSyncingAttachments || isSaveBlockedByValidation}
      >
        { buttonText }
      </button> }
    </div>
  );
});

Save.displayName = 'Save'; // Set the display name

Save.propTypes = {
  timeRecordObject: PropTypes.object,
  saveAll: PropTypes.bool,
  handleClick: PropTypes.func,
};

export default Save;
